import React, { ChangeEvent, useLayoutEffect, useRef } from "react";
import classNames from "classnames";

import "components/form/form.scss";
import "components/form/choiceList/choiceList.scss";
import "./checkbox.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMinus } from "@fortawesome/pro-solid-svg-icons";

export interface ICheckboxProps {
  label?: string | JSX.Element;
  helpText?: string;
  supportText?: string;
  disabled?: boolean;
  id?: string;
  labelHidden?: boolean;
  name?: string;
  value?: string | number | readonly string[];
  checked?: boolean;
  indeterminate?: boolean;
  circle?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  size?: "extra small" | "small" | "medium" | "large";
  ariaChecked?: boolean | "mixed";
  fullWidth?: boolean;
  borderStyle?: React.CSSProperties;
  darkStyle?: boolean;
  error?: boolean;
  readOnly?: boolean;
  subtitle?: string;
  selectionOutline?: boolean;
  tabIndex?: number;
}

const Checkbox: React.FC<ICheckboxProps> = props => {
  const {
    label,
    supportText,
    disabled,
    id,
    labelHidden,
    name,
    value,
    checked,
    indeterminate,
    circle,
    onChange,
    size,
    ariaChecked,
    fullWidth,
    readOnly,
    subtitle,
    selectionOutline,
    tabIndex,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    const node = inputRef.current;
    if (node) {
      node.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate, checked]);

  const resolvedAriaChecked = indeterminate === true ? "mixed" : ariaChecked !== undefined ? ariaChecked : checked;
  const passClickToParent = readOnly && !selectionOutline;

  const isSelected = Boolean(checked) || Boolean(indeterminate);

  if (circle) {
    return (
      <label
        className={classNames("ui-choice flex flex-0 align-center", {
          "ui-choice_label-hidden": labelHidden,
          "ui-checkbox-selection-outline": selectionOutline,
          "ui-checkbox-selection-outline-selected": selectionOutline && checked,
          "pointer-events-none": passClickToParent,
        })}
      >
        <span
          className={classNames("ui-checkbox_container flex flex-0 items-stretch", {
            "ui-checkbox-small": size === "small" || size === "extra small",
            "ui-checkbox-medium": size === "medium" || size === "large",
            "pointer-events-none": selectionOutline,
          })}
        >
          <span className="relative w-full ui-checkbox ui-checkbox-variant-segmented">
            <input
              ref={inputRef}
              id={id}
              type="checkbox"
              name={name}
              className="absolute top-0 overflow-hidden h-0.5 w-0.5 p-0 ui-checkbox_input"
              disabled={disabled}
              onChange={onChange}
              aria-invalid={props.error ? "true" : "false"}
              role="checkbox"
              aria-checked={resolvedAriaChecked}
              value={value}
              checked={checked}
              onClick={event => (selectionOutline ? null : event.stopPropagation())}
              readOnly={readOnly}
              tabIndex={tabIndex}
            />
            <span
              style={props?.borderStyle}
              className={classNames(
                "ui-checkbox-box block relative ui-checkbox_backdrop",
                disabled ? "ui-checkbox-disabled" : "",
                props.error ? "ui-checkbox-error" : "",
                props.darkStyle ? "ui-checkbox_darkstyle" : "",
              )}
            />
            <span className="ui-checkbox_segmented-radio-dot" aria-hidden />
          </span>
        </span>
        {label ? (
          <span
            className={classNames(
              "ui-checkbox-label",
              disabled && "ui-checkbox-label-disabled",
              props?.darkStyle && "ui-checkbox_darkstyle_label",
            )}
          >
            <span>{label}</span>
            {supportText ? <p className="ui-checkbox-label-support">{supportText}</p> : null}
          </span>
        ) : null}
      </label>
    );
  }

  return (
    <div
      className={classNames("flex items-center", {
        "ui-checkbox-selection-outline": selectionOutline,
        "ui-checkbox-selection-outline-selected": selectionOutline && checked,
        "pointer-events-none": passClickToParent,
      })}
      onClick={
        selectionOutline
          ? e => {
              e.stopPropagation();
              inputRef.current?.click();
            }
          : undefined
      }
    >
      <label
        className={classNames("ui-choice", {
          "ui-choice_label-hidden": labelHidden,
          "w-full": fullWidth,
          "pointer-events-none": selectionOutline,
        })}
      >
        <span
          className={classNames("ui-checkbox_container flex flex-0 items-stretch", {
            "ui-checkbox-small": size === "small" || size === "extra small",
            "ui-checkbox-medium": size === "medium" || size === "large",
          })}
        >
          <span
            className={classNames("ui-checkbox ui-checkbox-variant-default", {
              "ui-checkbox--indeterminate": indeterminate,
              "ui-checkbox--selected": isSelected,
            })}
          >
            <input
              ref={inputRef}
              id={id}
              type="checkbox"
              name={name}
              className={classNames("ui-checkbox_input", {
                "ui-checkbox-indeterminate": indeterminate && !checked,
              })}
              disabled={disabled}
              onChange={onChange}
              onClick={event => (selectionOutline ? null : event.stopPropagation())}
              aria-invalid={props.error ? "true" : "false"}
              role="checkbox"
              aria-checked={resolvedAriaChecked}
              value={value}
              checked={checked}
              readOnly={readOnly}
              tabIndex={tabIndex}
            />
            <span
              style={props?.borderStyle}
              className={classNames(
                "ui-checkbox-box block relative ui-checkbox_backdrop",
                disabled ? "ui-checkbox-disabled" : "",
                props.error ? "ui-checkbox-error" : "",
                props.darkStyle ? "ui-checkbox_darkstyle" : "",
              )}
            />
            <span
              className={classNames(
                "absolute top-1/2 left-1/2 pointer-events-none ui-checkbox_icon",
                disabled && "ui-checkbox_icon-disabled",
                size === "extra small" && "w-2 h-2",
              )}
            >
              {indeterminate && !checked ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faCheck} />}
            </span>
          </span>
        </span>
        <span
          className={classNames(
            "ui-checkbox-label",
            disabled && "ui-checkbox-label-disabled",
            props?.darkStyle && "ui-checkbox_darkstyle_label",
          )}
        >
          <span className="ui-checkbox-label-content">
            <span className="ui-checkbox-label-primary">{label}</span>
            {subtitle ? <span className="ui-checkbox-label-subtitle"> - {subtitle}</span> : null}
          </span>
          {supportText ? <p className="ui-checkbox-label-support">{supportText}</p> : null}
        </span>
      </label>
    </div>
  );
};

export default Checkbox;
